/**
 * Reciprocal Rank Fusion (RRF) for combining search results.
 *
 * RRF combines multiple ranked lists using only rank positions, not scores.
 * This avoids the problem of comparing incompatible scores (BM25 vs cosine similarity).
 *
 * Formula: RRF_score(item) = Σ 1/(k + rank) for each list containing the item
 *
 * Reference: Cormack, Clarke, Büttcher. "Reciprocal Rank Fusion Outperforms
 * Condorcet and Individual Rank Learning Methods" (SIGIR 2009)
 */
import type { EntityScope, ICategorizedHit, ISearchResult } from "./search.types";

const RRF_K = 60;

// Entity-resolver matches are deterministic, high-precision name/address hits, so
// weight them above probabilistic keyword/semantic results. Without this, a resolver
// hit (one list, rank 0) ties an ordinary hit and gets out-summed by items appearing
// in multiple runners (keyword + semantic), burying the exact vendor the user searched.
const RESOLVER_RRF_WEIGHT = 4;

function rrfSourceWeight(opId: string, hit: ICategorizedHit): number {
    const op = hit.sourceOps.find((o) => o.id === opId);
    return op?.stage === "resolver" ? RESOLVER_RRF_WEIGHT : 1;
}

function normalizeEntityName(entity: string): string {
    const lower = entity.toLowerCase().trim().replace(/[-_]/g, "");
    if (lower.endsWith("ies") && lower.length > 3) return lower.slice(0, -3) + "y";
    if (lower.endsWith("es") && lower.length > 2) {
        const withoutEs = lower.slice(0, -2);
        if (lower.match(/ches$|shes$|xes$|zes$/)) return withoutEs;
        if (lower.endsWith("ses") && !/[aeiou]$/.test(withoutEs)) return withoutEs;
    }
    if (lower.endsWith("s") && lower.length > 1) return lower.slice(0, -1);
    return lower;
}

function entityMatchesScope(hitEntity: string, scopedEntities: Set<string>): boolean {
    if (scopedEntities.size === 0) return true;
    const hitNorm = normalizeEntityName(hitEntity);
    return Array.from(scopedEntities).some((s) => normalizeEntityName(s) === hitNorm);
}

const SCOPE_PENALTIES: Record<EntityScope, number> = {
    specific: 0.7,
    multiple: 0.8,
    broad: 1.0,
};

interface IRankedHit {
    hit: ICategorizedHit;
    ranks: Map<string, number>;
}

export function mergeResultsRRF(
    prev: ICategorizedHit[],
    newResult: ISearchResult,
    entityScope: EntityScope,
    scopedEntities: Set<string>,
): ICategorizedHit[] {
    const byKey = new Map<string, IRankedHit>();

    for (const hit of prev) {
        const key = `${hit.entity}:${hit.id}`;
        byKey.set(key, {
            hit: { ...hit },
            ranks: new Map(hit._ranks ?? []),
        });
    }

    const op = newResult.op;
    for (let rank = 0; rank < newResult.hits.length; rank++) {
        const hit = newResult.hits[rank];
        const key = `${hit.entity}:${hit.id}`;

        const existing = byKey.get(key);
        if (existing) {
            existing.ranks.set(op.id, rank);
            if (!existing.hit.sourceOps.some((o) => o.id === op.id)) {
                existing.hit.sourceOps.push(op);
            }
        } else {
            const ranks = new Map<string, number>();
            ranks.set(op.id, rank);
            byKey.set(key, {
                hit: { ...hit, sourceOps: [op] },
                ranks,
            });
        }
    }

    const results: ICategorizedHit[] = [];

    for (const { hit, ranks } of Array.from(byKey.values())) {
        let rrfScore = 0;
        for (const [opId, rank] of Array.from(ranks.entries())) {
            rrfScore += rrfSourceWeight(opId, hit) / (RRF_K + rank + 1);
        }

        const matchesScope = entityMatchesScope(hit.entity, scopedEntities);
        if (!matchesScope && entityScope !== "broad") {
            rrfScore *= SCOPE_PENALTIES[entityScope];
        }

        const categorizedHit: ICategorizedHit = {
            ...hit,
            score: hit.score,
            rerank_score: rrfScore,
            _ranks: Array.from(ranks.entries()),
        };

        results.push(categorizedHit);
    }

    const hitMatchesScope = (h: ICategorizedHit) => {
        if (entityScope === "broad" || scopedEntities.size === 0) return true;
        return entityMatchesScope(h.entity, scopedEntities);
    };

    return results.sort((a, b) => {
        const aMatch = hitMatchesScope(a);
        const bMatch = hitMatchesScope(b);
        if (aMatch !== bMatch) return aMatch ? -1 : 1;
        return b.rerank_score - a.rerank_score;
    });
}
